import { ref } from "vue";
import { defineStore } from "pinia";
import { useSignalR } from "@/composables/signalr";
import {
  deleteApiV1PrizeSuggestionsById,
  getApiV1PrizeSuggestions,
  postApiV1PrizeSuggestions,
  putApiV1PrizeSuggestionsById,
  type CreatePrizeSuggestionDto,
  type PrizeSuggestionDto,
  type UpdatePrizeSuggestionDto,
} from "@/client";
import type {
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
} from "@/types/signalr-events";

export const usePrizeSettingsStore = defineStore("prizeSettings", () => {
  const settingsDataLoading = ref(false);
  const settingsDataLoaded = ref(false);
  const prizeSuggestions = ref<PrizeSuggestionDto[]>([]);
  const { subscribe, onReconnected } = useSignalR();
  // Live events sent while offline are lost; reload what this store already showed.
  onReconnected(() => {
    if (settingsDataLoaded.value) {
      fetchSettingsData().catch((err) => console.error("Resync after reconnect failed", err));
    }
  });

  async function fetchSettingsData() {
    settingsDataLoading.value = true;
    try {
      const response = await getApiV1PrizeSuggestions();
      if (response.error) {
        console.error("Error fetching prize suggestions:", response.error);
      }
      prizeSuggestions.value = response.data ?? [];
      settingsDataLoaded.value = true;
    } finally {
      settingsDataLoading.value = false;
    }
  }

  async function ensurePrizeSettingsDataLoaded() {
    if (!settingsDataLoaded.value) {
      await fetchSettingsData();
    }
  }

  async function createSuggestion(suggestion: CreatePrizeSuggestionDto) {
    const response = await postApiV1PrizeSuggestions({
      body: suggestion,
    });
    if (response.error) {
      console.error("Error creating prize suggestion:", response.error);
      throw new Error("Failed to create prize suggestion");
    }
    const newSuggestion = response.data!;
    return newSuggestion!;
  }

  async function updateSuggestion(
    suggestionId: string,
    suggestion: UpdatePrizeSuggestionDto,
  ) {
    const response = await putApiV1PrizeSuggestionsById({
      path: { id: suggestionId },
      body: suggestion,
    });
    if (response.error) {
      console.error("Error updating prize suggestion:", response.error);
      throw new Error("Failed to update prize suggestion");
    }
  }

  async function deleteSuggestion(id: string) {
    const response = await deleteApiV1PrizeSuggestionsById({
      path: { id },
    });
    if (response.error) {
      console.error("Error deleting prize suggestion:", response.error);
      throw new Error("Failed to delete prize suggestion");
    }
  }

  //#region SignalR
  subscribe<EntityUpdatedEvent<PrizeSuggestionDto>>(
    "PrizeSuggestionUpdated",
    async (event) => {
      console.debug("PrizeSuggestionUpdated event received:", event);
      createOrUpdatePrizeSuggestion(event.entity);
    },
  );
  subscribe<EntityDeletedEvent>("PrizeSuggestionDeleted", async (event) => {
    console.debug("PrizeSuggestionDeleted event received:", event);
    const index = prizeSuggestions.value.findIndex(
      (suggestion) => suggestion.id === event.entityId,
    );
    if (index !== -1) {
      prizeSuggestions.value.splice(index, 1);
    }
  });
  subscribe<EntityCreatedEvent<PrizeSuggestionDto>>(
    "PrizeSuggestionCreated",
    async (event) => {
      console.debug("PrizeSuggestionCreated event received:", event);
      createOrUpdatePrizeSuggestion(event.entity);
    },
  );

  function createOrUpdatePrizeSuggestion(suggestion: PrizeSuggestionDto) {
    const index = prizeSuggestions.value.findIndex(
      (s) => s.id === suggestion.id,
    );
    if (index === -1) {
      prizeSuggestions.value.push(suggestion);
    } else {
      prizeSuggestions.value[index] = suggestion;
    }
  }
  //#endregion

  return {
    prizeSuggestions,
    prizeSettingsDataLoading: settingsDataLoading,
    prizeSettingsDataLoaded: settingsDataLoaded,

    fetchPrizeSettingsData: fetchSettingsData,
    createPrizeSuggestion: createSuggestion,
    ensurePrizeSettingsDataLoaded,
    updatePrizeSuggestion: updateSuggestion,
    deletePrizeSuggestion: deleteSuggestion,
  };
});
